import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { AddButton } from "../Buttons/AddButton";
import { ButtonGroup } from "../Buttons/ButtonGroup";
import { CloseButton } from "../Buttons/CloseButton";
import { DeleteButton } from "../Buttons/DeleteButton";
import { DragAndDropButton } from "../Buttons/DragAndDropButton";
import { SaveButton } from "../Buttons/SaveButton";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import { AreaBody } from "../General/AreaBody";
import { ContentGroup } from "../General/ContentGroup";
import { LanguagePicker } from "../General/LanguagePicker";
import { Toolbar } from "../General/Toolbar";
import { InputBooleanComponent } from "../Inputs/InputBooleanComponent";
import { InputChoiseComponent } from "../Inputs/InputChoiseComponent";
import { InputNumberComponent } from "../Inputs/InputNumberComponent";
import { InputTextComponent } from "../Inputs/InputTextComponent";
import * as MetaManagement from "../MetaManagement";
import { FilterMode, LanguageInfo, InputText } from "../Shared";
import * as UiStore from "../States/AreaUiInfo";
import { AppStore } from "../Stores/AppStore";
import { DialogResult, DialogStore } from "../Stores/DialogStore";
import { DocumentFilter } from "../Stores/DocumentFilterStore";
import { DocumentSearchStore } from "../Stores/DocumentSearchStore";
import { MetaDataStore } from "../Stores/MetaDataStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { Strings } from "../Stores/StringsStore";
import { Urls } from "../Urls";
import * as Utilities from "../Utilities";
import { FolderIcon } from "../Icons/FolderIcon";


interface IMetaProps extends RouteComponentProps<IMetaPropsParams>, UiStore.IAreaUiInfo {
	store?: MetaDataStore;
}

interface IMetaPropsParams {
	id: string;
}

@inject('store')
@inject('areaUiInfo')
@observer
export class MetaEdit extends React.Component<IMetaProps, {}> {

	store: MetaDataStore;
	documentSearchStore: DocumentSearchStore = new DocumentSearchStore(DocumentFilter);
	@observable model = new MetaManagement.MetaDetails();
	@observable hash: string;
	@observable canSaveData: boolean = false;
	@observable selectedLanguage: string;

	componentWillMount() {
		this.store = this.props.store;
		this.canSaveData = false;
	}

	componentDidMount() {
		this.documentSearchStore.loadUiSettings();
		let uiLang = Utilities.getUiLanguage();
		if (!uiLang) {
			uiLang = AppStore.context.userInterfaceLanguages[0].name;
		}
		this.selectedLanguage = uiLang;
		this.getModel();
		this.setCurrentItem();
	}

	componentDidUpdate(prevProps: IMetaProps) {
		if (this.props.match.params.id !== prevProps.match.params.id) {
			this.getModel();
			this.store.metaValuesMoveMode = false;
			this.setCurrentItem();
		}
	}

	@action setModel(model: MetaManagement.MetaDetails) {
		this.model = this.fillMissing(model);
		this.hash = this.getHashCode();
		NotificationStore.clearMessage();
		AppStore.isBusyModelLoading = false;
	}

	@action getModel() {
		AppStore.isBusyModelLoading = true;
		this.canSaveData = false;
		let query = new MetaManagement.MetaDetailsQuery();
		query.guid = this.props.match.params.id;
		query.language = this.selectedLanguage;
		MetaManagement.getMetaAsync(query).then(model => {
			this.setModel(model);
		}).catch(error => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
		});
	}

	@action setCurrentItem = () => {
		this.store.currentFocusedItem = this.props.match.params.id;
	}

	canSaveModel = () => {
		let newHash = this.getHashCode();
		let hasChanged = this.hash !== newHash;
		let canSave = hasChanged;
		if (!this.model.id.isValid || !this.model.iirdsId.isValid || !this.model.caption.isValid || !this.model.regex.isValid) {
			canSave = false;
		}
		if (this.model.predefined.value && this.model.metaValues.length === 0) {
			canSave = false;
		}
		for (let i = 0; i < this.model.metaValues.length; i++) {
			if (!this.model.metaValues[i].value.isValid || (!this.model.metaValues[i].value.value && !this.model.metaValues[i].value.valueNeedsTranslation)) {
				canSave = false;
				break;
			}
		}
		if (this.error) {
			canSave = false;
		}
		if (!this.model.caption.value.trim() && !this.model.caption.valueNeedsTranslation) {
			canSave = false;
		}
		AppStore.isDirtyChange = hasChanged;
		AppStore.isDirtyMode = canSave;
		AppStore.saveOnLeave = this.saveOnDirtyLeave;
		return canSave;
	}

	private getHashCode(): string {
		let dataType = this.model.dataType.value;
		let canBeLanguageDependent = dataType === "Text";
		let canBeMultiple = dataType === "Date" || dataType === "DocumentLink" || dataType === "Hierarchy" || dataType === "Number" || dataType === "Text";
		let canBePredefined = dataType === "Hierarchy" || dataType === "Number" || dataType === "Text";
		let canHaveRegex = dataType === "Hierarchy" || dataType === "Text";
		let metaValuesArray = new Array();
		this.model.metaValues.forEach(mv => {
			let metaValueObject = {
				"parentId": this.getHashCodeFromString(mv.parentId),
				"value": this.getHashCodeFromString(mv.value.value)
			}
			metaValuesArray.push(metaValueObject);
		});
		let hashObject = {
			"caption": this.getHashCodeFromString(this.model.caption.value),
			"dataType": this.getHashCodeFromString(this.model.dataType.value),
			"filterMaxValues": this.model.filterMaxValues.value,
			"filterMode": this.getHashCodeFromString(this.model.filterMode.value),
			"id": this.getHashCodeFromString(this.model.id.value),
			"iirdsId": this.getHashCodeFromString(this.model.iirdsId.value),
			"languageDependent": canBeLanguageDependent && this.model.languageDependent.value,
			"metaValues": JSON.stringify(metaValuesArray),
			"multiple": canBeMultiple && this.model.multiple.value,
			"predefined": canBePredefined && this.model.predefined.value,
			"regex": canHaveRegex ? this.getHashCodeFromString(this.model.regex.value) : "",
			"required": this.model.required.value,
			"showAsColumn": this.model.showAsColumn.value,
			"showAsFilter": this.model.showAsFilter.value,
			"showAsInfo": this.model.showAsInfo.value
		};
		return JSON.stringify(hashObject);
	}

	private getHashCodeFromString(str: string): string {
		return str ? str : "";
	}

	filterMode = () => {
		let mode = FilterMode.Single;
		switch (this.model.filterMode.value) {
			case "Single":
				mode = FilterMode.Single;
				break;
			case "MultipleAnd":
				mode = FilterMode.MultipleAnd;
				break;
			case "MultipleOr":
				mode = FilterMode.MultipleOr;
				break;
		}
		return mode;
	}

	handleSaveUi = () => {
		this.saveModel(true);
	}

	saveOnDirtyLeave = (onSaveCompleted: () => void) => {
		this.saveModel(false, onSaveCompleted);
	}

	removeDocumentFilterIfShowAsFilterUnTicked = (metaGuid: string) => {
		DocumentFilter.deleteFilterByKey(metaGuid);
	}

	private saveModel(update: boolean, onSaveCompleted?: () => void) {
		if (this.canSaveModel()) {
			AppStore.isBusy = true;
			let updateModel = new MetaManagement.MetaDetailsUpdate();
			updateModel.lastModified = this.model.lastModified;
			updateModel.captionLastModified = this.model.captionLastModified;
			updateModel.guid = this.model.guid;
			updateModel.language = this.selectedLanguage;
			updateModel.id = this.model.id.value;
			updateModel.iirdsId = this.model.iirdsId.value;
			updateModel.caption = this.model.caption.value;
			updateModel.captionNeedsTranslation = this.model.caption.valueNeedsTranslation;
			updateModel.required = this.model.required.value;
			if (updateModel.dataType !== "DocumentLink") {
				updateModel.showAsColumn = this.model.showAsColumn.value;
				updateModel.showAsFilter = this.model.showAsFilter.value;
				updateModel.filterMaxValues = this.model.filterMaxValues.value;
				updateModel.filterMode = this.filterMode();
			}
			updateModel.showAsInfo = this.model.showAsInfo.value;

			updateModel.dataType = this.model.dataType.value;
			switch (updateModel.dataType) {
				case "Boolean":
					break;
				case "Date":
					updateModel.multiple = this.model.multiple.value;
					break;
				case "DocumentLink":
					updateModel.multiple = this.model.multiple.value;
					break;
				case "Hierarchy":
					updateModel.multiple = this.model.multiple.value;
					updateModel.languageDependent = this.model.languageDependent.value;
					updateModel.predefined = true;
					break;
				case "Number":
					updateModel.multiple = this.model.multiple.value;
					updateModel.predefined = this.model.predefined.value;
					break;
				case "Text":
					updateModel.multiple = this.model.multiple.value;
					updateModel.languageDependent = this.model.languageDependent.value;
					updateModel.regex = this.model.regex.value;
					updateModel.predefined = this.model.predefined.value;
					break;
			}

			this.model.metaValues.forEach((metaValue: MetaManagement.MetaValue) => {
				let metaValueUpdate = new MetaManagement.MetaValueUpdate();
				metaValueUpdate.guid = metaValue.guid;
				metaValueUpdate.languageLastModified = metaValue.languageLastModified;
				metaValueUpdate.lastModified = metaValue.lastModified;
				metaValueUpdate.parentId = metaValue.parentId;
				metaValueUpdate.value = metaValue.value.value;
				metaValueUpdate.valueNeedsTranslation = metaValue.value.valueNeedsTranslation;
				updateModel.metaValues.push(metaValueUpdate);
			});

			MetaManagement.insertOrUpdateMetaAsync(updateModel).then(model => {
				AppStore.isBusy = false;
				if (model.isValid) {
					this.hash = this.getHashCode();
					this.store.isEditMode = true;
					this.canSaveData = this.canSaveModel();
					//remove saved filter if show as filter unticked, from readerUiInfo Local storage user story - 3372, Bug - 3373
					if (!updateModel.showAsFilter) {
						this.removeDocumentFilterIfShowAsFilterUnTicked(updateModel.guid);
					}
					this.store.search();
					NotificationStore.showSuccessMessage(Strings.general.saveSuccessMessage);
				}
				else {
					AppStore.isBusy = false;
					DialogStore.showError(Strings.general.errorDialogTitle, model.validationMessage);
				}
				if (update) {
					this.model = this.fillMissing(model);
				}
				if (onSaveCompleted) {
					onSaveCompleted();
				}
			}).catch(error => {
				AppStore.isBusy = false;
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
		}

	}

	handleDelete = () => {
		let caption: string;
		if (this.model.caption.valueNeedsTranslation) {
			caption = this.model.caption.placeHolder.match(":.*}")[0];
			caption = caption.substr(2);
			caption = caption.substr(0, caption.length - 1);
		}
		else {
			caption = this.model.caption.value;
		}
		DialogStore.show(Utilities.format(Strings.general.dialogDeleteTitle, caption), Utilities.format(Strings.general.dialogDeleteText, caption)).then(result => {
			if (result === DialogResult.Yes) {
				AppStore.isBusy = true;
				MetaManagement.removeMetaAsync({ guid: this.model.guid, lastModified: this.model.lastModified }).then(() => {
					AppStore.isBusy = false;
					this.store.search();
					NotificationStore.showSuccessMessage(Strings.general.deleteSuccessMessage);
					this.store.isEditMode = false;
					this.store.currentFocusedItem = "";
					AppStore.history.replace(Urls.metas);
				}).catch(error => {
					AppStore.isBusy = false;
					DialogStore.showError(Strings.general.errorDialogTitle, error);
				});
			}
		});
	}

	handleCancel = () => {
		AppStore.keepOnPage(Urls.metas, null, this.store.areaUiInfo.closeExpand);
	}

	handleChange = () => {
		this.canSaveData = this.canSaveModel();
		NotificationStore.clearMessage();
	}

	handleRegexChange = () => {
		let regex: RegExp;
		try {
			regex = new RegExp("^" + this.model.regex.value + "$");
			this.model.regex.isValid = true;
			this.model.regex.validationMessage = null;
		}
		catch (e) {
			regex = null;
			this.model.regex.isValid = false;
		}
		let firstInvalidMetaValue: MetaManagement.MetaValue = null;
		for (let i = 0; i < this.model.metaValues.length; i++) {
			this.model.metaValues[i].value.regex = this.model.regex.value;
			if (this.model.regex.value && (!this.model.metaValues[i].value.valueNeedsTranslation || this.model.metaValues[i].value.value)) {
				if (regex) {
					let isValid = regex.test(this.model.metaValues[i].value.value);
					this.model.metaValues[i].value.isValid = isValid;
					if (!isValid && !firstInvalidMetaValue) {
						firstInvalidMetaValue = this.model.metaValues[i];
					}
				}
			}
			else {
				this.model.metaValues[i].value.isValid = true;
				this.model.metaValues[i].value.validationMessage = null;
			}
		}
		if (!this.model.predefined.value) {
			if (firstInvalidMetaValue) {
				this.model.regex.isValid = false;
				if (!this.model.regex.validationMessage) {
					this.model.regex.validationMessage = firstInvalidMetaValue.value.validationMessage;
				}
			}
		}
		this.handleChange();
	}

	handleDataTypeChange = () => {
		if (this.model.dataType.value === "Hierarchy") {
			this.model.predefined.value = true;
			this.model.predefined.isReadOnly = true;
		}
		if (this.model.dataType.value === "DocumentLink") {
			this.model.showAsInfo.value = true;
		}
		this.handleRegexChange();
	}

	handlePredefinedChange = () => {
		if (this.model.predefined.value && this.model.metaValues.length === 0) {
			this.model.predefined.isValid = false;
			this.model.predefined.validationMessage = Strings.metaManagement.metaEdit.metaEditEmptyPredefinedValuesResultText;
		} else {
			this.model.predefined.isValid = true;
			this.model.predefined.validationMessage = "";
		}
		this.handleRegexChange();
	}

	@observable error: string;

	handleHierarchyChange = (value: string) => {
		this.error = null;
		let values: string[] = value.match(/.*/gm).filter(v => v);
		if (values.length > 0) {
			let newValues: MetaManagement.MetaValue[] = [];
			let parents: string[] = [];
			for (let i = 0; i < values.length && !this.error; i++) {
				let v = values[i];
				if (!v) {
					this.error = Strings.metaManagement.metaEdit.hierarchyBlankLine;
					break;
				}
				let matches = v.match(/^(= *)*/).filter(m => m);
				let dots: string = matches.length > 0 ? matches[0] : "";
				if (dots.search(" ") > -1) {
					this.error = Strings.metaManagement.metaEdit.hierarchyBlankBeforeNodeName;
					break;
				}
				if (dots.length > parents.length) {
					this.error = Strings.metaManagement.metaEdit.hierarchySkippedLevel;
					break;
				}
				let nodeName = v.replace(dots, "").trim();
				if (!nodeName) {
					this.error = Strings.metaManagement.metaEdit.hierarchyEmptyNodeName;
					break;
				}
				let index = nodeName.search(/[<>?":|\\\/*=]/);
				if (index > -1) {
					let character = nodeName.substr(index, 1);
					this.error = Utilities.format(Strings.metaManagement.metaEdit.hierarchyInvalidCharacter, character);
					break;
				}
				while (dots.length < parents.length) {
					parents.pop();
				}
				let metaValue;
				let existingValues = this.model.metaValues.filter(mv => mv.value.value === nodeName);
				if (existingValues.length === 0) {
					metaValue = new MetaManagement.MetaValue();
					metaValue.guid = Utilities.newGuid();
					metaValue.canDelete = true;
					metaValue.value.isReadOnly = false;
					metaValue.value.isValid = true;
					metaValue.value.value = nodeName;
					metaValue.parentId = parents.length > 0 ? parents[parents.length - 1] : null;
				}
				else {
					metaValue = existingValues[0];
					metaValue.parentId = parents.length > 0 ? parents[parents.length - 1] : null;
				}
				newValues.push(metaValue);
				if (dots.length === parents.length) {
					parents.push(metaValue.guid);
				}
			}
			if (this.error) {
				this.handleChange();
			}
			else {
				this.model.metaValues.clear();
				this.model.metaValues.push(...newValues);
				this.handleChange();
			}
		}
		else {
			this.model.metaValues.clear();
			this.handleChange();
		}
	}

	@computed get showRegex(): boolean {
		return this.model.dataType.value === "Text";
	}

	@computed get showLanguageDependent(): boolean {
		return this.model.dataType.value === "Text";
	}

	@computed get showMultiple(): boolean {
		return this.model.dataType.value !== "Boolean";
	}

	@computed get showPredifined(): boolean {
		return this.model.dataType.value === "Text" || this.model.dataType.value === "Number" || this.model.dataType.value === "Hierarchy";
	}

	@computed get showShowAsColumn(): boolean {
		return this.model.dataType.value !== "DocumentLink";
	}

	@computed get showShowAsFilter(): boolean {
		return this.model.dataType.value !== "DocumentLink";
	}

	@computed get showFilterMode(): boolean {
		return this.model.dataType.value !== "DocumentLink" && this.model.dataType.value !== "Hierarchy";
	}

	@computed get showFilterMaxValues(): boolean {
		return this.model.dataType.value !== "DocumentLink" && this.model.dataType.value !== "Hierarchy";
	}

	get languageOptions() {
		let result: LanguageInfo[] = [];
		AppStore.context.contentLanguages
			.sort((a, b) => {
				if (a.caption < b.caption) return -1;
				if (a.caption > b.caption) return 1;
				return 0;
			})
			.forEach(lang => result.push(lang));
		return result;
	}

	fillMissing(model: MetaManagement.MetaDetails) {
		model.dataType.label = Strings.metaManagement.metaEdit.dataType;
		model.id.label = Strings.metaManagement.metaEdit.id;
		model.id.placeHolder = Strings.metaManagement.metaEdit.idPlaceHolder;
		model.iirdsId.label = Strings.metaManagement.metaEdit.iirdsId;
		model.iirdsId.placeHolder = Strings.metaManagement.metaEdit.iirdsIdPlaceHolder;
		model.caption.label = Strings.metaManagement.metaEdit.caption;
		if (!model.caption.placeHolder) {
			model.caption.placeHolder = Strings.metaManagement.metaEdit.captionPlaceHolder;
		}
		model.regex.label = Strings.metaManagement.metaEdit.regex;
		model.regex.placeHolder = Strings.metaManagement.metaEdit.regexPlaceHolder;
		model.predefined.label = Strings.metaManagement.metaEdit.preDefined;
		model.multiple.label = Strings.metaManagement.metaEdit.multiple;
		model.required.label = Strings.metaManagement.metaEdit.required;
		model.languageDependent.label = Strings.metaManagement.metaEdit.languageDependent;
		model.showAsColumn.label = Strings.metaManagement.metaEdit.showAsColumn;
		model.showAsFilter.label = Strings.metaManagement.metaEdit.showAsFilter;
		model.showAsInfo.label = Strings.metaManagement.metaEdit.showAsInfo;
		model.filterMaxValues.label = Strings.metaManagement.metaEdit.filterMaxValues;
		model.filterMode.label = Strings.metaManagement.metaEdit.filterMode;
		model.filterMaxValues.placeHolder = Strings.metaManagement.metaEdit.filterMaxValuesPlaceHolder;
		for (let i = 0; i < model.metaValues.length; i++) {
			model.metaValues[i].value.regex = model.regex.value;
		}
		return model;
	}

	@observable addedValue: MetaManagement.MetaValue;

	handleAdd = () => {
		let newValue = new MetaManagement.MetaValue();
		newValue.guid = Utilities.newGuid();
		newValue.canDelete = true;
		newValue.value.isReadOnly = false;
		newValue.value.isValid = false;
		newValue.value.value = "";
		newValue.value.regex = this.model.regex.value;
		this.addedValue = newValue;
		this.model.metaValues.push(newValue);
		this.handlePredefinedChange();
	}

	moveButtonClick = () => {
		this.store.metaValuesMoveMode = !this.store.metaValuesMoveMode;
	}

	@computed get canAdd(): boolean {
		return (this.model.predefined.isReadOnly || this.store.metaValuesMoveMode);
	}

	@computed get areaAsideButtonTooltip() {
		return this.props.areaUiInfo.isCollapsed ? Strings.metaManagement.metaEdit.areaAsideButtonCollapsedTooltip : Strings.metaManagement.metaEdit.areaAsideButtonExpandedTooltip;
	}

	onLanguageSelect = (option: LanguageInfo) => {
		if (option.name !== this.selectedLanguage) {
			if (this.canSaveModel()) {
				DialogStore.show(Strings.general.keepOnPageDialogTitle, Strings.general.keepOnPageDialogText).then((result: DialogResult) => {
					if (result === DialogResult.Yes) {
						this.saveModel(false, () => this.setLanguage(option.name));
					}
					if (result === DialogResult.No) {
						this.setLanguage(option.name);
					}
				});
			}
			else {
				this.setLanguage(option.name);
			}
		}
	}

	private setLanguage(language: string) {
		this.selectedLanguage = language;
		this.getModel();
	}

	render() {
		let regex = this.showRegex ? <InputTextComponent model={this.model.regex} onChange={this.handleRegexChange} testId="regexInput" /> : null;
		let languageDependent = this.showLanguageDependent ? <InputBooleanComponent model={this.model.languageDependent} onChange={this.handleChange} testId="languageDependentRadioCheck" /> : null;
		let multiple = this.showMultiple ? <InputBooleanComponent model={this.model.multiple} onChange={this.handleChange} testId="multipleRadioCheck" /> : null;
		let predefined = this.showPredifined ? <InputBooleanComponent model={this.model.predefined} onChange={this.handlePredefinedChange} testId="predefinedRadioCheck" /> : null;
		let values = this.showPredifined && this.model.predefined.value ?
			<div>
				{this.model.dataType.value !== "Hierarchy"
					? <MetaValuesEdit focus={this.addedValue} handleAdd={this.handleAdd} handleChange={this.handlePredefinedChange} values={this.model.metaValues}
						readonly={this.model.predefined.isReadOnly} number={this.model.dataType.value === "Number"} isMovable={this.store.metaValuesMoveMode} />
					: <MetaValuesEditHierarchy values={this.model.metaValues} handleChange={this.handleHierarchyChange} error={this.error} />}
			</div> : null;
		let lang = AppStore.context.contentLanguages.filter(l => l.name === this.selectedLanguage)[0];

		return <AreaBody loading={AppStore.isBusyModelLoading}>
			<Toolbar>
				<ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
				<SaveButton onClick={this.handleSaveUi} disabled={!this.canSaveData} testId="saveButton" />
				<DeleteButton onClick={this.handleDelete} testId="deleteButton" disabled={!this.store.isEditMode || this.model.isGlobal} />
				<LanguagePicker options={this.languageOptions} value={this.selectedLanguage} onChange={this.onLanguageSelect} />
				<span className="buttongroup is--buttongroup-right">
					<CloseButton onClick={this.handleCancel} testId="closeButton" />
				</span>
			</Toolbar>
			<div className="area-grid__body-content">
				<div className="content-main">
					<h1>{this.store.isEditMode ? Strings.metaManagement.metaEdit.title : Strings.metaManagement.metaEdit.addMetaTitle}</h1>
					<div>
						<ContentGroup caption={Strings.metaManagement.metaEdit.generalCaption} uiContext={this.props.areaUiInfo}>
							<InputTextComponent model={this.model.caption} onChange={this.handleChange} testId="captionInput" languageDependent={true} languageCaption={lang && lang.caption} region={lang && lang.region} required={true} />
							{!this.model.isGlobal ? <InputTextComponent model={this.model.id} onChange={this.handleChange} testId="idInput" /> : null}
							{!this.model.isGlobal ? <InputTextComponent model={this.model.iirdsId} onChange={this.handleChange} testId="iirdsIdInput" /> : null}
						</ContentGroup>
						{!this.model.isGlobal ? <ContentGroup caption={Strings.metaManagement.metaEdit.definitionCaption} uiContext={this.props.areaUiInfo}>
							<InputChoiseComponent model={this.model.dataType} onChange={this.handleDataTypeChange} testId="dataTypeSelect" />
							<div>
								{multiple}
							</div>
							<div>
								{languageDependent}
							</div>
							{regex}
							<div>
								{predefined}
							</div>
						</ContentGroup> : null}
						<ContentGroup caption={Strings.metaManagement.metaEdit.functionalityCaption} uiContext={this.props.areaUiInfo}>
							{!this.model.isGlobal && this.showShowAsColumn ? <div>
								<InputBooleanComponent model={this.model.showAsColumn} onChange={this.handleChange} testId="showAsColumnRadioCheck" />
							</div> : null}
							{this.showShowAsFilter ? <div>
								<InputBooleanComponent model={this.model.showAsFilter} onChange={this.handleChange} testId="showAsFilterRadioCheck" />
							</div> : null}
							{!this.model.isGlobal ? <div>
								<InputBooleanComponent model={this.model.showAsInfo} onChange={this.handleChange} testId="showAsInfoRadioCheck" />
							</div> : null}
							{this.showFilterMode ? <InputChoiseComponent model={this.model.filterMode} onChange={this.handleChange} testId="filterModeSelect" /> : null}
							{this.showFilterMaxValues ? <InputNumberComponent model={this.model.filterMaxValues} locale={AppStore.context.sessionDetails.uiLanguage} onChange={this.handleChange} testId="filterMaxValueInput" /> : null}
						</ContentGroup>
						{
							values ? <ContentGroup caption={Strings.metaManagement.metaEdit.preDefinedValues} languageDependent={this.model.languageDependent.value} languageCaption={lang && lang.caption} region={lang && lang.region} uiContext={this.props.areaUiInfo}>
								{this.model.dataType.value !== "Hierarchy" ? <ButtonGroup>
									<AddButton title={Strings.metaManagement.metaEdit.valuesAddButtonTooltip} onClick={this.handleAdd} disabled={this.canAdd} testId="addPredefinedValueButton" />
									<DragAndDropButton title={Strings.metaManagement.metaEdit.valuesMoveButtonTooltip} onClick={this.moveButtonClick} disabled={this.model.predefined.isReadOnly} pressed={this.store.metaValuesMoveMode} testId="metaValuesMoveButton" />
									<span className="tinytool__splitter"></span>
								</ButtonGroup> : null}
								{values}
							</ContentGroup> : null
						}
					</div>
				</div>
			</div>
		</AreaBody>;
	}
}

interface IMetaValuesProps {
	values: MetaManagement.MetaValue[];
	readonly: boolean;
	number: boolean;
	handleChange: () => void;
	handleAdd: () => void;
	isMovable?: boolean;
	focus: MetaManagement.MetaValue;
}

@observer
class MetaValuesEdit extends React.Component<IMetaValuesProps, {}> {

	moveFrom: number = 0;

	handleDelete = (item: MetaManagement.MetaValue) => {
		for (let i = 0; i < this.props.values.length; i++) {
			if (this.props.values[i].guid === item.guid) {
				this.props.values.splice(i, 1);
				this.props.handleChange();
				return;
			}
		}
	}

	handleNoPredefinedValuesSpanClick = () => {
		this.props.handleAdd();
	}

	handleDragStart = (event: React.DragEvent<any>) => {
		let elem = (event.target as HTMLLIElement);
		this.moveFrom = Number(elem.getAttribute("data-id"));

		// this is needed for Firefox
		event.dataTransfer.setData("text", "dummy");

		// Only move is allowed
		event.dataTransfer.effectAllowed = "move";
	}

	handleDragOver = (event: React.DragEvent<any>) => {
		event.preventDefault();
	}

	handleDragEnter = (event: React.DragEvent<any>) => {
		event.preventDefault();
		event.dataTransfer.effectAllowed = "move";
	}

	handleDrop = (event: React.DragEvent<any>) => {
		event.preventDefault();
		let elem = (event.target as HTMLLIElement);

		let moveTo = Number(elem.getAttribute("data-id"));
		this.props.values.splice(moveTo, 0, this.props.values.splice(this.moveFrom, 1)[0]);
		this.props.handleChange();
	}

	handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		if (event.keyCode === 13) {
			this.props.handleAdd();
		}
	}

	render() {
		return this.props.values.length > 0

			? <fieldset className="various-value">
				<ul data-test-id="metaValueList">
					{
						this.props.values.map((item, index) => {
							return <li key={item.guid} className="various-value__item">
								{
									this.props.isMovable
										? <span data-id={index} draggable={true} onDragStart={this.handleDragStart} onDragOver={this.handleDragOver} onDragEnter={this.handleDragEnter} onDrop={this.handleDrop} className="draggable-various-value">{item.value.value}</span>
										: <InputTextComponent focus={this.props.focus === item} onChange={this.props.handleChange} model={item.value} testName="metaValueInput" onKeyDown={this.handleKeyDown} />
								}
								{!this.props.isMovable ? <span className="various-value__item__additional">
									<div className="tinytool">
										<DeleteButton title={Strings.metaManagement.metaEdit.valuesRemoveButtonTooltip} onClick={this.handleDelete.bind(this, item)} disabled={!item.canDelete} testName="removeMetaValueButton" />
									</div>
								</span> : null
								}
							</li>;
						})
					}
				</ul>
			</fieldset>

			: <div className="inside__note is--clickable-note" onClick={this.handleNoPredefinedValuesSpanClick}>
				<span>
					{Strings.metaManagement.metaEdit.metaEditEmptyPredefinedValuesResultText}
				</span>
			</div>;
	}
}

interface IMetaValuesPropsHierarchy {
	values: MetaManagement.MetaValue[];
	handleChange: (value: string) => void;
	error: string;
}

@observer
class MetaValuesEditHierarchy extends React.Component<IMetaValuesPropsHierarchy, {}> {

	@observable isActive: boolean;
	@observable editedValue: string;

	handleNoPredefinedValuesSpanClick = () => {
		this.editedValue = this.value;
		this.isActive = true;
	}

	onBlur = () => {
		if (!this.props.error) {
			this.isActive = false;
			this.editedValue = null;
		}
	}

	onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		this.editedValue = event.target.value;
		this.props.handleChange(this.editedValue);
	}

	@computed get isValid(): boolean {
		return true;
	}

	@computed get value(): string {
		if (this.editedValue) {
			return this.editedValue;
		}
		let value = "";
		this.props.values.forEach(v => {
			let level = "";
			let parent = this.props.values.filter(p => p.guid === v.parentId)[0];
			while (parent) {
				level += "=";
				parent = this.props.values.filter(p => p.guid === parent.parentId)[0];
			}
			if (value) {
				value += "\n";
			}
			value += level + v.value.value;
		});
		return value;
	}

	render() {
		return this.props.values.length === 0 && !this.isActive

			? <div className="inside__note hierarchy is--clickable-note" onClick={this.handleNoPredefinedValuesSpanClick}>
				<span>
					{Strings.metaManagement.metaEdit.hierarchyNoValuesText}
					<br />
					<br />
					{Strings.metaManagement.metaEdit.hierarchyNoValuesLevel} 1<br />
					= {Strings.metaManagement.metaEdit.hierarchyNoValuesLevel} 2<br />
					== {Strings.metaManagement.metaEdit.hierarchyNoValuesLevel} 3<br />
				</span>
			</div>

			: this.isActive

				? <>
					<textarea
						onChange={this.onChange}
						className={!this.isValid ? "is--input-invalid" : null}
						value={this.value}
						data-test-id="hierarchy-value-active"
						onBlur={this.onBlur}
						style={{ minHeight: 300, minWidth: "100%", borderColor: this.props.error ? "red" : "transparent" }}
						autoFocus={true} />
					{this.props.error ? <span className="is--validation-note">{this.props.error}</span> : null}
				</>

				: <fieldset className="various-value" onClick={this.handleNoPredefinedValuesSpanClick}>
					<ul data-test-id="metaValueList">
						{
							this.props.values.map((item, index) => {
								let level = 0;
								let parentId = item.parentId;
								while (parentId) {
									level++;
									let parent = this.props.values.filter(v => v.guid === parentId)[0];
									parentId = parent.parentId;
								}
								let showFolder = this.props.values.some(v => v.parentId === item.guid);
								return <li key={item.guid} className="various-value__item hierarchyitem" style={{ marginLeft: level * 20 + (!showFolder ? 21 : 0) }}>
									{showFolder ? <FolderIcon /> : null}
									<label>{item.value.value}</label>
								</li>;
							})
						}
					</ul>
				</fieldset>;
	}
}